import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, History } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const TABS = [
  { id: "products", label: "Products", hint: "Create or update products with variants (size, stock, pricing, SEO)" },
  { id: "inventory", label: "Inventory", hint: "Update stock for existing variant SKUs" },
  { id: "pricing", label: "Pricing & Discounts", hint: "Update MRP, selling price, scheduled sale prices by SKU" },
];

const download = (path, filename) => api.get(path, { responseType: "blob" }).then((r) => {
  const u = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = u; a.download = filename; a.click();
});

function BulkTab({ tab }) {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || busy) return;
    setBusy(true); setResult(null); setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/bulk/${tab}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data);
      if (data.summary.invalid_rows > 0) toast.warning(`${data.summary.invalid_rows} row(s) have errors — they will be skipped`);
      else toast.success(`${data.summary.valid_rows} rows valid — review and confirm`);
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const confirm = async () => {
    if (!batch || confirming) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/admin/bulk/${tab}/confirm`, { batch_id: batch.batch_id });
      setResult(data.summary);
      toast.success(`Applied: ${JSON.stringify({ created: data.summary.created, updated: data.summary.updated })}`);
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setConfirming(false); }
  };

  const s = batch?.summary;
  return (
    <div data-testid={`bulk-${tab}`}>
      <div className="bg-white border border-[#E5DEC9] p-5 mb-5">
        <p className="text-sm text-[#6E6D6A] mb-4">{TABS.find((t) => t.id === tab)?.hint}</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download(`/admin/bulk/template/${tab}`, `${tab}_template.csv`)} data-testid={`bulk-${tab}-template`} className="tq-btn-outline !py-2.5">
            <Download size={14} /> Download Template
          </button>
          {tab === "inventory" && (
            <button onClick={() => download("/admin/bulk/inventory/export", "inventory_export.csv")} data-testid="bulk-inventory-export" className="tq-btn-outline !py-2.5">
              <FileSpreadsheet size={14} /> Export Current Inventory
            </button>
          )}
          {tab === "products" && (
            <button onClick={() => download("/admin/export/products", "products_export.csv")} data-testid="bulk-products-export" className="tq-btn-outline !py-2.5">
              <FileSpreadsheet size={14} /> Export Products
            </button>
          )}
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`bulk-${tab}-upload`} className="tq-btn-dark !py-2.5">
            <Upload size={14} /> {busy ? "Validating…" : "Upload CSV / Excel"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={upload} className="hidden" data-testid={`bulk-${tab}-file`} />
        </div>
      </div>

      {batch && (
        <div className="bg-white border border-[#E5DEC9] p-5 mb-5" data-testid="bulk-preview">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {[["Total Rows", s.total_rows], ["Valid", s.valid_rows], ["Invalid", s.invalid_rows],
              ["Duplicates", s.duplicate_skus ?? "—"], ["Products", s.products_detected ?? "—"]].map(([l, v]) => (
              <div key={l} className="border border-[#E5DEC9] p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#8C7A5E]">{l}</p>
                <p className={`font-serif text-2xl mt-1 ${l === "Invalid" && v > 0 ? "text-[#8B1E21]" : ""}`}>{v}</p>
              </div>
            ))}
          </div>
          <div className="max-h-72 overflow-y-auto border border-[#F4EFE6]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#F4EFE6]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C7A5E]">
                  <th className="p-2">Row</th><th className="p-2">SKU / Key</th>
                  {tab === "inventory" && <><th className="p-2">Current</th><th className="p-2">New</th></>}
                  {tab === "pricing" && <th className="p-2">Discount</th>}
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {batch.rows.map((r) => (
                  <tr key={r.row} className={`border-t border-[#F4EFE6] ${r.errors.length ? "bg-[#8B1E21]/5" : ""}`} data-testid={`bulk-row-${r.row}`}>
                    <td className="p-2">{r.row}</td>
                    <td className="p-2 font-mono">{r.key || "—"}</td>
                    {tab === "inventory" && <><td className="p-2">{r.current ?? "—"}</td><td className="p-2 font-semibold">{r.new}</td></>}
                    {tab === "pricing" && <td className="p-2">{r.discount_percent != null ? `${r.discount_percent}% off` : "—"}</td>}
                    <td className="p-2">
                      {r.errors.length
                        ? <span className="text-[#8B1E21] flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{r.errors.join("; ")}</span>
                        : <span className="text-[#1A3C34] flex items-center gap-1"><CheckCircle2 size={12} /> OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={confirm} disabled={confirming || s.valid_rows === 0} data-testid="bulk-confirm-btn" className="tq-btn-dark">
              {confirming ? "Applying…" : `Confirm Import (${s.valid_rows} valid rows)`}
            </button>
            {s.invalid_rows > 0 && (
              <button onClick={() => download(`/admin/bulk/${batch.batch_id}/errors`, "error_report.csv")} data-testid="bulk-errors-btn" className="tq-btn-outline">
                <Download size={14} /> Error Report
              </button>
            )}
            <button onClick={() => setBatch(null)} className="tq-btn-outline">Discard</button>
          </div>
          <p className="text-[11px] text-[#9a938a] mt-3">Invalid rows are never imported. Existing data is not modified by invalid rows.</p>
        </div>
      )}

      {result && (
        <div className="bg-[#1A3C34]/10 border border-[#1A3C34]/30 p-5 mb-5" data-testid="bulk-result">
          <p className="font-semibold text-[#1A3C34] mb-2">Import completed</p>
          <p className="text-sm text-[#1A3C34]">
            {result.created != null && `${result.created} created · `}{result.updated ?? 0} updated · {result.failed ?? 0} failed
          </p>
        </div>
      )}
    </div>
  );
}

export default function AdminBulk() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "products";
  const [history, setHistory] = useState([]);
  useEffect(() => { api.get("/admin/bulk/history").then(({ data }) => setHistory(data)).catch(() => {}); }, [tab]);
  return (
    <div data-testid="admin-bulk">
      <h1 className="font-serif text-3xl mb-2">Bulk Upload Center</h1>
      <p className="text-sm text-[#6E6D6A] mb-6">Upload → Validate → Preview → Confirm. Nothing changes until you confirm.</p>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })} data-testid={`bulk-tab-${t.id}`}
            className={`tq-btn !py-2 !px-4 ${tab === t.id ? "bg-[#121212] text-white" : "border border-[#E5DEC9] bg-white"}`}>{t.label}</button>
        ))}
      </div>
      <BulkTab key={tab} tab={tab} />
      {history.length > 0 && (
        <div className="bg-white border border-[#E5DEC9] p-5 mt-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4 flex items-center gap-2"><History size={14} /> Import History</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#8C7A5E] border-b border-[#E5DEC9]">
              <th className="py-2">When</th><th className="py-2">Type</th><th className="py-2">File</th><th className="py-2">Rows</th><th className="py-2">Status</th><th className="py-2">By</th>
            </tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-[#F4EFE6]" data-testid={`bulk-history-${h.id}`}>
                  <td className="py-2.5 text-xs">{new Date(h.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2.5 capitalize">{h.type}</td>
                  <td className="py-2.5 text-xs max-w-[140px] truncate">{h.filename}</td>
                  <td className="py-2.5">{h.summary?.valid_rows ?? 0}/{h.summary?.total_rows ?? 0}</td>
                  <td className="py-2.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${h.status === "confirmed" ? "bg-[#1A3C34]/10 text-[#1A3C34]" : "bg-[#F4EFE6] text-[#8C7A5E]"}`}>{h.status}</span></td>
                  <td className="py-2.5 text-xs text-[#9a938a]">{h.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
